import ClearIcon from '@mui/icons-material/Clear';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import MuiSelect, { type SelectProps as MuiSelectProps } from '@mui/material/Select';
import {
  forwardRef,
  useId,
  type ReactElement,
  type ReactNode,
  type RefAttributes
} from 'react';
import { Checkbox } from '../Checkbox';
import { IconButton } from '../IconButton';
import type { FieldAppearance } from '../fieldAppearance';
import { SelectMultipleChips } from './SelectMultipleChips';
import {
  resolveSelectChipColors,
  type SelectChipBackgroundColor
} from './selectChipBackground';
import {
  SELECT_ALL_DISPLAY_LABEL,
  areAllRegularOptionsSelected,
  excludeSelectAllValues,
  getRegularOptionValues,
  getRegularOptions,
  getSelectAllCheckboxState,
  optionFromWireValue,
  optionsFromWireValues,
  resolveClearedValue,
  wireOptionValue,
  wireOptionValues
} from './utils';
import { useTheme } from '@mui/material/styles';

export type SelectOption<T extends string | number = string> = {
  label: ReactNode;
  value: T;
  disabled?: boolean;
  /** When `true` on a `multiple` select, choosing this row toggles all other options. Its `value` is not stored. */
  selectAll?: boolean;
  /** Only used for `variant="chip"` — MUI palette token (e.g. `primary`, `warning`). */
  backgroundColor?: SelectChipBackgroundColor;
  icon?: ReactNode;
};

export type SelectOptionsConstraint = readonly SelectOption<string | number>[];

/** Union of option `value`s — drives `value` / `onChange` typing from `options`. */
export type SelectOptionValue<O extends SelectOptionsConstraint> = O[number]['value'];

export type SelectSharedProps<O extends SelectOptionsConstraint> = {
  label?: string;
  /**
   * `default` — grey field + label above.
   * `plain` — white field + label above (input variant only).
   * `borderless` — no background/border, just the value (input variant only).
   * @default 'default'
   */
  appearance?: FieldAppearance;
  /** @default 'input' */
  variant?: 'input' | 'chip';
  helperText?: ReactNode;
  /** Shown below the field when `error` is true. Takes precedence over `helperText`. */
  errorMessage?: ReactNode;
  options: O;
  /** Only used for `variant="chip"` — MUI palette token (e.g. `primary`, `warning`). */
  chipBackgroundColor?: SelectChipBackgroundColor;
  /** Show checkboxes in the menu when `multiple` is true. @default true when `multiple` */
  checkbox?: boolean;
  disableClearButton?: boolean;
};

/** Props whose names and types match MUI `Select` — sourced from `SelectProps` directly. */
type SelectPropsFromMui = Pick<
  MuiSelectProps,
  'disabled' | 'onBlur' | 'onFocus' | 'fullWidth' | 'error' | 'placeholder' | 'displayEmpty' | 'required' | 'readOnly'
>;

type SelectSingleValue<O extends SelectOptionsConstraint> = SelectOptionValue<O> | undefined;

export type SelectSingleProps<
  O extends SelectOptionsConstraint,
  V extends SelectOptionValue<O> | undefined = SelectOptionValue<O> | undefined
> = SelectSharedProps<O> &
  SelectPropsFromMui & {
    multiple?: false;
    value?: V;
    defaultValue?: V;
    onChange?: (value: V) => void;
    /** Custom closed-field display. Receives the typed option value, not the wired DOM string. */
    renderValue?: (value: V) => ReactNode;
  };

export type SelectMultipleProps<
  O extends SelectOptionsConstraint,
  V extends readonly SelectOptionValue<O>[] = SelectOptionValue<O>[]
> = SelectSharedProps<O> &
  SelectPropsFromMui & {
    multiple: true;
    value?: V;
    defaultValue?: V;
    onChange?: (value: V) => void;
    /** Custom closed-field display for the selected values. */
    renderValue?: (value: V) => ReactNode;
  };

export type SelectProps<
  O extends SelectOptionsConstraint,
  V extends SelectOptionValue<O> | readonly SelectOptionValue<O>[] | undefined = SelectOptionValue<O> | undefined
> = SelectSingleProps<O, V extends SelectOptionValue<O> | undefined ? V : SelectOptionValue<O> | undefined> | SelectMultipleProps<O, V extends readonly SelectOptionValue<O>[] ? V : SelectOptionValue<O>[]>;

/** @deprecated Use `SelectSingleProps` / `SelectMultipleProps` with options-based
 *  (`SelectOptionValue<O>`) instead of an explicit generic. */
export type SelectPropsLegacy<T extends string | number = string> = SelectSingleProps<
  readonly SelectOption<T>[]
>;

type ResolvedSelectProps<O extends SelectOptionsConstraint> = (SelectSingleProps<O> | SelectMultipleProps<O>) &
  SelectSharedProps<O> & {
    multiple?: boolean;
  };

function renderOptionLabel<T extends string | number>(
  option: SelectOption<T>,
  selected: boolean,
  showCheckbox: boolean,
  options?: { indeterminate?: boolean; onCheckboxToggle?: () => void }
): ReactNode {
  if (!showCheckbox) {
    return option.label;
  }

  const onCheckboxToggle = options?.onCheckboxToggle;

  return (
    <span className="SmSelectMenu-item">
      <span
        className="SmSelectMenu-itemCheckbox"
        style={onCheckboxToggle ? undefined : { pointerEvents: 'none' }}
      >
        <Checkbox
          checked={selected}
          indeterminate={options?.indeterminate}
          tabIndex={-1}
          onChange={
            onCheckboxToggle
              ? event => {
                  event.stopPropagation();
                  onCheckboxToggle();
                }
              : undefined
          }
        />
      </span>
      {typeof option.label === 'string' ? (
        <span className="SmSelectMenu-itemLabel">{option.label}</span>
      ) : (
        option.label
      )}
    </span>
  );
}

function renderDefaultMultipleValue<O extends SelectOptionsConstraint>(
  typedValues: SelectOptionValue<O>[],
  options: O,
  placeholder?: string
): ReactNode {
  const regularValues = excludeSelectAllValues(typedValues, options);

  if (regularValues.length === 0 && placeholder) {
    return <span className="SmSelect-placeholder SmSelect-value">{placeholder}</span>;
  }

  if (getRegularOptions(options).length > 0 && areAllRegularOptionsSelected(regularValues, options)) {
    return <span className="SmSelect-value">{SELECT_ALL_DISPLAY_LABEL}</span>;
  }

  return <SelectMultipleChips values={regularValues} options={getRegularOptions(options)} />;
}

/** Design-system Select styled to match `Input` look & feel. */
function SelectInner<O extends SelectOptionsConstraint>(
  props: SelectSingleProps<O> | SelectMultipleProps<O>,
  ref: React.ForwardedRef<HTMLDivElement>
): ReactElement {
  type T = SelectOptionValue<O>;

  const {
    label,
    appearance = 'default',
    variant = 'input',
    fullWidth = true,
    onChange,
    onBlur,
    onFocus,
    disabled = false,
    readOnly = false,
    error = false,
    helperText,
    errorMessage,
    placeholder,
    displayEmpty,
    options = [],
    renderValue: renderValueProp,
    chipBackgroundColor,
    checkbox,
    multiple = false,
    required = false,
    disableClearButton = false,
  } = props as ResolvedSelectProps<O>;

  const showMenuCheckboxes = multiple && (checkbox ?? true);
  const selectAllOptions = multiple ? options.filter(option => option.selectAll) : [];
  const selectableOptions = multiple ? getRegularOptions(options) : options;
  const hasSelectAllOption = selectAllOptions.length > 0;

  const value = props.value;
  const defaultValue = props.defaultValue;
  const multipleValue = multiple
    ? excludeSelectAllValues(value as SelectOptionValue<O>[] | undefined, options)
    : [];

  const theme = useTheme();
  const fieldId = useId();
  const resolvedFullWidth = fullWidth ?? variant === 'input';
  const isPlain = appearance === 'plain' && variant === 'input';
  const isBorderless = appearance === 'borderless' && variant === 'input';
  const resolvedPlaceholder = placeholder;
  const hasLabel = Boolean(label?.trim());
  const matchedOptionBg =
    !multiple && value !== undefined && value !== ''
      ? options.find(option => wireOptionValue(option.value) === wireOptionValue(value as SelectSingleValue<O>))
          ?.backgroundColor
      : undefined;
  const resolvedChipColors = resolveSelectChipColors(chipBackgroundColor ?? matchedOptionBg, theme);
  const style =
    variant === 'chip'
      ? ({
          '--sm-select-chip-bg': resolvedChipColors.background,
          '--sm-select-chip-fg': resolvedChipColors.foreground
        } as React.CSSProperties)
      : undefined;
  const inputBaseClassName = [
    'SmInputBase-root',
    isPlain ? 'SmInputBase-appearance-plain' : null,
    isBorderless ? 'SmInputBase-appearance-borderless' : null
  ]
    .filter(Boolean)
    .join(' ');

  const selectedWiredSet = new Set(
    multiple
      ? wireOptionValues(multipleValue)
      : [wireOptionValue(value as SelectSingleValue<O>)]
  );

  const hasValue = multiple ? multipleValue.length > 0 : value !== undefined && value !== '';

  const wrapValue = (content: ReactNode) =>
    isPlain || isBorderless ? <span className="SmSelect-value">{content}</span> : content;

  const emitMultipleChange = (nextValues: SelectOptionValue<O>[]) => {
    (onChange as SelectMultipleProps<O>['onChange'])?.(
      excludeSelectAllValues(nextValues, options)
    );
  };

  const handleToggleSelectAll = () => {
    const regularValues = getRegularOptionValues(options);
    const nextValue = areAllRegularOptionsSelected(multipleValue, options) ? [] : regularValues;
    emitMultipleChange(nextValue);
  };

  const endAdornment =
    !disableClearButton &&
    variant === 'input' && hasValue && !disabled && !readOnly ? (
      <InputAdornment position="end" className="SmInputAdornment-root">
        <span onMouseDown={e => e.stopPropagation()}>
          <IconButton
            size="small"
            variant="text"
            color="secondary"
            aria-label="נקה"
            onClick={e => {
              e.stopPropagation();
              if (multiple) {
                emitMultipleChange([]);
              } else {
                (onChange as SelectSingleProps<O>['onChange'])?.(resolveClearedValue(options));
              }
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        </span>
      </InputAdornment>
    ) : undefined;

  const footerText = error && errorMessage ? errorMessage : helperText;
  const resolvedDisplayEmpty =
    displayEmpty ?? (Boolean(resolvedPlaceholder) || Boolean(renderValueProp) || multiple);

  return (
    <FormControl variant="standard" fullWidth={resolvedFullWidth} disabled={disabled ?? readOnly} error={error} required={required}>
      {hasLabel ? (
        <InputLabel className="SmInputLabel-root" shrink htmlFor={fieldId}>
          {label}
        </InputLabel>
      ) : null}
      <MuiSelect
        ref={ref}
        multiple={multiple}
        className={[
          'SmSelect-root',
          variant === 'chip' ? 'SmSelect-variant-chip' : null,
          isPlain ? 'SmSelect-appearance-plain' : null,
          isBorderless ? 'SmSelect-appearance-borderless' : null,
          multiple ? 'SmSelect-multiple' : null
        ]
          .filter(Boolean)
          .join(' ')}
        id={fieldId}
        value={
          multiple
            ? wireOptionValues(multipleValue)
            : value !== undefined
              ? wireOptionValue(value as T)
              : ''
        }
        defaultValue={
          multiple
            ? undefined
            : defaultValue !== undefined
              ? wireOptionValue(defaultValue as T)
              : undefined
        }
        onChange={e => {
          const raw = e.target.value;
          if (multiple) {
            const typedValues = excludeSelectAllValues(
              optionsFromWireValues(raw, selectableOptions),
              options
            );
            emitMultipleChange(typedValues);
            return;
          }
          const key = String(raw);
          const resolved = optionFromWireValue(key, options);
          (onChange as SelectSingleProps<O>['onChange'])?.(resolved as SelectSingleValue<O>);
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        style={style}
        aria-label={
          isPlain || isBorderless ? (hasLabel ? label : (resolvedPlaceholder ?? undefined)) : undefined
        }
        input={
          variant === 'input' ? (
            <InputBase className={inputBaseClassName} endAdornment={endAdornment} />
          ) : (
            <InputBase
              className={[
                'SmSelectChipInput-root',
                'SmSelect-variant-chip',
                resolvedFullWidth ? 'SmSelectChipInput-fullWidth' : null
              ]
                .filter(Boolean)
                .join(' ')}
            />
          )
        }
        displayEmpty={resolvedDisplayEmpty}
        renderValue={selected => {
          if (multiple) {
            const wired = (typeof selected === 'string' ? selected.split(',') : selected) as string[];
            const typedValues = excludeSelectAllValues(
              optionsFromWireValues(wired, selectableOptions),
              options
            );

            if (renderValueProp) {
              return (renderValueProp as SelectMultipleProps<O>['renderValue'])?.(typedValues);
            }

            if (resolvedPlaceholder && typedValues.length === 0) {
              return (
                <span className="SmSelect-placeholder SmSelect-value">{resolvedPlaceholder}</span>
              );
            }

            if (hasSelectAllOption && areAllRegularOptionsSelected(typedValues, options)) {
              return wrapValue(SELECT_ALL_DISPLAY_LABEL);
            }

            return renderDefaultMultipleValue(typedValues, options, resolvedPlaceholder);
          }

          const wired = String(selected ?? '');
          const typedValue = optionFromWireValue(wired, options) as SelectSingleValue<O>;

          if (renderValueProp) {
            return (renderValueProp as SelectSingleProps<O>['renderValue'])?.(typedValue);
          }

          if (resolvedPlaceholder && (wired === '' || selected === undefined)) {
            return (
              <span className="SmSelect-placeholder SmSelect-value">{resolvedPlaceholder}</span>
            );
          }
          const matchedOption = options.find(option => wireOptionValue(option.value) === wired);
          const content = matchedOption ? matchedOption.label : String(selected ?? '');
          return wrapValue(content);
        }}
        MenuProps={{
          PaperProps: { className: 'SmSelectMenu-paper' },
          MenuListProps: {
            className: ['SmSelectMenu-list', multiple ? 'SmSelectMenu-list--multiple' : 'SmSelectMenu-list--single']
              .filter(Boolean)
              .join(' ')
          }
        }}
      >
        {multiple &&
          selectAllOptions.map((opt, index) => {
            const { checked, indeterminate } = getSelectAllCheckboxState(multipleValue, options);

            return (
              <MenuItem
                key={`select-all-${index}`}
                disabled={opt.disabled}
                selected={checked}
                className="SmSelectMenu-itemRoot--selectAll"
                sx={
                  checked
                    ? {
                        backgroundColor: theme.palette.primary.light,
                        '&:hover': { backgroundColor: theme.palette.primary.light },
                        '&.Mui-focusVisible': { backgroundColor: theme.palette.primary.light }
                      }
                    : undefined
                }
                onMouseDown={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleToggleSelectAll();
                }}
              >
                {renderOptionLabel(opt, checked, showMenuCheckboxes, { indeterminate })}
              </MenuItem>
            );
          })}
        {multiple && hasSelectAllOption && selectableOptions.length > 0 ? (
          <Divider className="SmSelectMenu-divider" />
        ) : null}
        {(multiple ? selectableOptions : options).map(opt => {
          const wired = wireOptionValue(opt.value);
          const isSelected = selectedWiredSet.has(wired);

          return (
            <MenuItem key={wired} value={wired} disabled={opt.disabled}>
              {renderOptionLabel(opt, isSelected, showMenuCheckboxes)}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {footerText ? <FormHelperText className="SmInputHelperText-root">{footerText}</FormHelperText> : null}
    </FormControl>
  );
}

export const Select = forwardRef(SelectInner) as {
  <O extends SelectOptionsConstraint, V extends SelectOptionValue<O> | undefined>(
    props: SelectSingleProps<O, V> & { multiple?: false } & RefAttributes<HTMLDivElement>
  ): ReactElement;
  <O extends SelectOptionsConstraint, V extends readonly SelectOptionValue<O>[]>(
    props: SelectMultipleProps<O, V> & { multiple: true } & RefAttributes<HTMLDivElement>
  ): ReactElement;
};

export type { SelectChipBackgroundColor, SelectChipPaletteColor } from './selectChipBackground';
export {
  resolveSelectChipBackground,
  resolveSelectChipColors,
  getContrastTextForBackground
} from './selectChipBackground';
